import {
  IIlp,
  IlpService,
  IUser,
  ServiceStatusEnum,
  PathwayService,
  CreatePathwayRequestBody,
  SearchPathwayRequestBody
} from "@imaginelearning/mps-api-service";
import {
  classMap,
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxBackground,
  UxBackgroundEnum,
  UxBrandEnum,
  UxIcon,
  UxIconEnum,
  UxNavbar,
  UxOrientationEnum,
  UxPositionEnum,
  UxPriorityEnum,
  UxSizeExtendedEnum,
  useMathJax
} from "@imaginelearning/mps-ux-component-library";
import { IlpContext } from "context-providers/ilp.provider";
import { PathwayContext } from "context-providers/pathway.provider";
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import EdgePathways from "components/edge-pathways/edge-pathways";
import useGradeRange from "hooks/usegraderange";
import useStudentLoginDetails from "components/liftoff/hooks/useStudentLoginDetails";
import MyAssignmentsContainer from "../components/assignments/my-assignments-container";
import UserRewardStars from "../components/user-reward-stars/user-reward-stars";
import EdgeUserProfile from "../components/edge-user-profile/edge-user-profile";
import { RewardContext } from "../context-providers/reward.provider";
import { UserContext } from "../context-providers/user.provider";

import "./home-container.scss";
import useMPPSubject from "../components/liftoff/hooks/useMPPSubject";
import useTheme from "../hooks/usetheme";

const EdgeLessons = lazy(
  () => import("../components/edge-lessons/edge-lessons" /* webpackChunkName: "edge-lessons" */)
);

const EdgeRewards = lazy(
  () => import("../components/edge-rewards/edge-rewards" /* webpackChunkName: "edge-rewards" */)
);

const spinner = <UxIcon icon={UxIconEnum.CIRCLE_NOTCH} />;

interface IProps {
  authTokenSubject?: string;
  authTokenIsPractice?: boolean;
  authTokenIsAssignment?: boolean;
}

function HomeContainer(props: IProps) {
  const { authTokenSubject, authTokenIsAssignment, authTokenIsPractice } = props;
  const { userState } = useContext(UserContext);
  const { getReward } = useContext(RewardContext);
  const { ilpState, setIlpId } = useContext(IlpContext);
  const { updateMppSubject } = useMPPSubject();
  const { fetchPathway } = useContext(PathwayContext);
  const { theme } = useTheme();
  const { is6to12, is9to12 } = useGradeRange();
  const pathName = useLocation().pathname;
  const navigate = useNavigate();
  const [prevSelectedSubject, setPrevSelectedSubject] = useState("");
  const user: IUser = userState.response as IUser;
  const assignmentsPath = "/assignments";
  const rewards = pathName === "/rewards";
  const mystuff = pathName === "/mystuff";
  const isAssignments = pathName === assignmentsPath;
  const progress = pathName.includes("/myprogress");
  const { showIMPathway } = useStudentLoginDetails();

  useMathJax();

  useEffect(() => {
    // change the document lang tag to match the student's language preference
    const lang =
      user?.spanishLanguage?.includes((authTokenSubject ?? "none")?.toLowerCase()) ||
      user?.spanishLanguage?.includes(authTokenSubject ?? "none")
        ? "es-US"
        : "en-US";
    document.documentElement.lang = lang;
  }, [authTokenSubject, user]);

  useEffect(() => {
    const landingSubject = sessionStorage.getItem("landingSubject");
    if (landingSubject) {
      updateMppSubject(landingSubject);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      getReward(user.id);
      const fetchIlpOrPathwaySubjectsData = async () => {
        if (!authTokenIsAssignment) {
          if (authTokenIsPractice) {
            const gradeLevel = user.gradeLevel === 0 ? `K` : `${user.gradeLevel}`;
            const requestBody: SearchPathwayRequestBody = {
              gradeLevel,
              subject: authTokenSubject ?? "",
              state: user.organization.state,
              schoolYear: user.schoolYear ?? 0
            };
            const result = await PathwayService.searchPathway(requestBody, user.id);
            if (result.response.pathways.length > 0) {
              const selectedPathway: any = result.response.pathways[0];
              await fetchPathway(user.id, selectedPathway.studentPathwayId);
              if (!rewards && !progress && !mystuff && !isAssignments) navigate("/pathways");
            } else {
              // if no pathways are found, create a standard pathway using endpoint (this will be done via eventing later)
              const createRequest: CreatePathwayRequestBody = {
                gradeLevel,
                subject: authTokenSubject ?? "",
                state: user.organization.state,
                schoolYear: user.schoolYear ?? 0
              };
              const createResult = await PathwayService.createPathway(createRequest, user.id);
              if (createResult.status === ServiceStatusEnum.LOADED) {
                const selectedPathway: any = createResult.response;
                await fetchPathway(user.id, selectedPathway.studentPathwayId);
                if (!rewards && !progress && !mystuff && !isAssignments) navigate("/pathways");
              }
            }
          }
          if (!authTokenIsPractice) {
            const result = await IlpService.getIlps(user.id, user.schoolYear, authTokenSubject);
            if (result && result.response && result.response.length > 0) {
              const selectedSubject: any = result.response[0];
              setIlpId(selectedSubject.id || "");
              if (!rewards && !progress && !mystuff && !isAssignments) navigate("/lessons");
            }
          }
        } else if (!rewards && !progress && !mystuff) navigate(assignmentsPath);
      };
      if (authTokenSubject !== prevSelectedSubject) {
        setPrevSelectedSubject(authTokenSubject || "");
        fetchIlpOrPathwaySubjectsData();
      }
    }
  }, [
    user,
    getReward,
    authTokenSubject,
    authTokenIsAssignment,
    setIlpId,
    navigate,
    rewards,
    progress,
    mystuff,
    isAssignments,
    prevSelectedSubject
  ]);

  const lessons = pathName === "/lessons" || pathName === "/pathways";
  const baseClasses = "d-flex text-xxl text-white me-2";
  const headerlogo = {
    icon: UxBrandEnum.IMAGINELEARNING,
    size: UxSizeExtendedEnum.X4,
    className: baseClasses,
    squared: true,
    alt: "Imagine My Path"
  };
  let title = "MyPath";
  let ariaLabel = "Imagine My Path";
  if (authTokenIsPractice) {
    title = `${authTokenSubject} Mastery`;
    ariaLabel = "subject";
  } else if (authTokenSubject === "Math" && showIMPathway) {
    title = `${authTokenSubject} Builder`;
    ariaLabel = "subject";
  } else if (authTokenSubject === "Math") {
    title = authTokenSubject;
    ariaLabel = "subject";
  } else if ((ilpState?.response as IIlp)?.productTitle) {
    title = (ilpState.response as IIlp).productTitle;
    ariaLabel = "subject";
  }

  const lessonsLogo = {
    type: "custom" as const,
    icon: UxIconEnum.MY_PATH_LOGO,
    title,
    ariaLabel,
    size: UxSizeExtendedEnum.X4,
    className: classMap(baseClasses, "fw-normal filters brightness-10"),
    logoClassName: "text-white"
  };

  const handleNavigation = (path: string, state?: {}) => navigate(path, { state });

  const children = [
    {
      item: !is9to12 ? <UserRewardStars onStarBarClick={() => handleNavigation("rewards")} /> : <div />
    },
    {
      item: userState.status === ServiceStatusEnum.LOADED && user.selectedAvatar && (
        <UxAvatar
          avatar={user.selectedAvatar as UxAvatarEnum}
          avatarType={UxAvatarPriorityEnum.DEFAULT}
          size={UxSizeExtendedEnum.X1}
          avatarStyle={UxAvatarStyleEnum.BODY}
          className={`active-avatar d-flex ${is9to12 ? "with-border" : ""}`}
          theme={theme}
        />
      )
    },
    {
      item: (
        <EdgeUserProfile
          userFirstname={user?.firstname || ""}
          avatar={user.selectedAvatar as UxAvatarEnum}
          handleNavigation={handleNavigation}
        />
      )
    }
  ];

  const getBackground = () => {
    if (typeof user === "object") {
      return (
        <UxBackground
          background={
            user.selectedBackground ? (user.selectedBackground as UxBackgroundEnum) : UxBackgroundEnum.DEFAULT
          }
          className={classMap("position-fixed z-index--1", is6to12 ? "background-right-bottom" : "")}
          theme={theme}
        />
      );
    }
    return null;
  };

  return (
    <div className={classMap("home-container w-100 h-100", String(theme).replace(/_/g, "-"))}>
      {getBackground()}
      <UxNavbar
        color={UxPriorityEnum.NAVBAR}
        orientation={UxOrientationEnum.HORIZONTAL}
        items={children}
        brand={{ ...headerlogo, ...(lessons || rewards || progress || mystuff ? lessonsLogo : {}) }}
        className={`text-lg fw-bold home-navbar ${rewards || progress ? "rewards-navbar" : ""}`}
        bgClassName="opacity opacity-80"
        fixed={UxPositionEnum.TOP}
      />
      <div
        className={`body-content h-100 overflow-auto ${rewards || progress || mystuff ? "rewards-body-content" : ""}`}
        id="body-content"
        role="main"
      >
        <Suspense fallback={spinner}>
          <Routes>
            <Route
              path="rewards"
              element={
                <EdgeRewards
                  isAssignment={authTokenIsAssignment}
                  handleNavigation={(path: string) => handleNavigation(path)}
                />
              }
            />
            <Route
              path="myprogress"
              element={
                <EdgeRewards
                  isAssignment={authTokenIsAssignment}
                  handleNavigation={(path: string) => handleNavigation(path)}
                />
              }
            />
            <Route
              path="/myprogress/:subject"
              element={
                <EdgeRewards
                  isAssignment={authTokenIsAssignment}
                  handleNavigation={(path: string) => handleNavigation(path)}
                />
              }
            />
            <Route
              path="mystuff"
              element={
                <EdgeRewards
                  isAssignment={authTokenIsAssignment}
                  handleNavigation={(path: string) => handleNavigation(path)}
                />
              }
            />
            <Route path="/assignments" element={<MyAssignmentsContainer />} />
            <Route path="lessons" element={<EdgeLessons authTokenSubject={authTokenSubject} />} />
            <Route path="pathways" element={<EdgePathways authTokenSubject={authTokenSubject} />} />
            <Route
              path="/"
              element={
                authTokenIsPractice ? (
                  <EdgePathways authTokenSubject={authTokenSubject} />
                ) : (
                  <EdgeLessons authTokenSubject={authTokenSubject} />
                )
              }
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default HomeContainer;
